<template>
    <tr :key="item.id"
        class="tw-border-b tw-border-solid tw-border-gray-100 last:tw-border-b-0 tw-group even:tw-bg-gray-50/50 hover:tw-bg-primary-50/60 tw-duration-100">
        
        <!-- Render cells in the correct order as specified by visibleColumns -->
        <template v-for="(colName) in orderedVisibleColumns" :key="colName">
            <!-- ID column -->
            <td v-if="colName === 'id'" class="tw-px-5 tw-py-3 tw-text-sm">{{ item.id }}</td>
            
            <!-- Customer name column -->
            <td v-else-if="colName === 'customer_name'" class="tw-px-5 tw-py-3 tw-text-sm">
                <p class="tw-truncate">{{ item.customer_name }}</p>
            </td>
            
            <!-- Customer phone column -->
            <td v-else-if="colName === 'customer_phone'" class="tw-px-5 tw-py-3 tw-text-sm">
                <p class="tw-truncate tw-max-w-[100px]">{{ item.customer_phone }}</p>
            </td>
            
            <!-- Customer city column -->
            <td v-else-if="colName === 'customer_city'" class="tw-px-5 tw-py-3 tw-text-sm">
                <p class="tw-truncate tw-max-w-[70px]">{{ item.customer_city }}</p>
            </td>
            
            <!-- Customer area column -->
            <td v-else-if="colName === 'customer_area'" class="tw-px-5 tw-py-3 tw-text-sm">
                <p class="tw-truncate tw-max-w-[70px]">{{ item.customer_area || '-' }}</p>
            </td>
            
            <!-- Customer address column -->
            <td v-else-if="colName === 'customer_address'" class="tw-px-5 tw-py-3 tw-text-sm">
                <p class="tw-truncate tw-max-w-[120px]">{{ item.customer_address || '-' }}</p>
            </td>
            
            <!-- Customer notes column -->
            <td v-else-if="colName === 'customer_notes'" class="tw-px-5 tw-py-3 tw-text-sm">
                <p class="tw-truncate tw-max-w-[120px]">{{ item.customer_notes || '-' }}</p>
            </td>
            
            <!-- Order summary column -->
            <td v-else-if="colName === 'order_summary'" class="tw-px-5 tw-py-3 tw-text-sm">
                <div dir="rtl" class="tw-flex tw-flex-col tw-gap-1 tw-text-sm">
                    <div v-for="i in item.items" :key="i.id + i.updated_at" class="tw-flex tw-items-center tw-gap-2">
                        <p class="tw-bg-gray-50 tw-text-gray-600 tw-border tw-border-solid tw-border-gray-200 tw-px-1 tw-text-xs tw-rounded">{{ i.quantity }}</p>
                        <span class="tw-font-bold tw-text-primary-500">x</span>
                        <p class="tw-max-w-[250px] tw-whitespace-nowrap ">{{ i.product_name }}</p>
                        <p v-if="i.variant_name" class="tw-max-w-[150px]  tw-text-primary-500 tw-font-bold tw-text-xs tw-bg-primary-50 tw-px-2 tw-rounded tw-border tw-border-solid tw-border-primary-200">{{ i.variant_name }}</p>
                    </div>
                </div>
            </td>
            
            <!-- Price column -->
            <td v-else-if="colName === 'price'" class="tw-px-5 tw-py-3 tw-text-sm">
                <p class="tw-truncate tw-max-w-[70px] tw-font-bold tw-flex tw-items-center tw-gap-1">
                    <span>{{ item.items.reduce((p, c) => p + parseFloat(c.price), 0).toFixed(2) }}</span>
                    <span>{{ $t('currencies.' + CURRENCY, 'USD') }}</span>
                </p>
            </td>
            
            <!-- Calls column -->
            <td v-else-if="colName === 'calls' && active.followup" class="tw-px-5 tw-py-3 tw-text-sm">
                <p class="tw-truncate tw-max-w-[70px] tw-font-bold tw-flex tw-items-center tw-gap-1 tw-px-2 tw-text-cyan-500">
                    {{ item.followup_calls }}
                </p>
            </td>
            <td v-else-if="colName === 'calls'" class="tw-px-5 tw-py-3 tw-text-sm">
                <p class="tw-truncate tw-max-w-[70px] tw-font-bold tw-flex tw-items-center tw-gap-1 tw-px-2 tw-text-cyan-500">
                    {{ item.calls }}
                </p>
            </td>
            
            <!-- Followup status column -->
            <td v-else-if="colName === 'followup_status' && active.followup" class="tw-px-5 tw-py-3 tw-text-sm">
                <p class="tw-truncate tw-max-w-[100px] tw-text-xs tw-rounded tw-w-fit tw-px-4 tw-py-1 tw-mx-auto " :class="[reconfirmation.color]" v-if="reconfirmation?.name">
                    {{ $t(reconfirmation.name) }}
                </p>
            </td>
            
            <!-- Agent status column -->
            <td v-else-if="colName === 'agent_status' && !active.followup" class="tw-px-5 tw-py-3 tw-text-sm">
                <p class="tw-truncate tw-max-w-[100px] tw-text-xs tw-rounded tw-w-fit tw-px-4 tw-py-1 tw-mx-auto " :class="[confirmation.color]" v-if="confirmation?.name">
                    {{ $t(confirmation.name) }}
                </p>
            </td>
            
            <!-- Delivery ID column -->
            <td v-else-if="colName === 'delivery_id'" class="tw-px-5 tw-py-3 tw-text-sm">
                <div class="tw-flex tw-items-center tw-gap-2 tw-mx-auto tw-justify-center">
                    <template v-if="item.delivery_id">
                        <div class="tw-flex tw-items-center tw-justify-center tw-gap-2 tw-w-fit  tw-p-1  tw-bg-gray-100 tw-px-4 tw-rounded-md tw-border tw-border-solid tw-border-gray-200 hover:tw-border-gray-400 tw-duration-200 tw-cursor-pointer">
                            <p  class="tw-truncate tw-max-w-[100px] tw-font-bold tw-text-xs tw-rounded tw-w-fit">
                                {{ delivery.name }}
                            </p>
                        </div>
                        <a v-if="item.external_tracking && false" target="_blank" :href="getOrderLink(item.external_tracking)">
                            <icon icon="mynaui:external-link" class="tw-text-base group-hover:tw-text-primary-500 tw-text-gray-400 tw-duration-200" />
                        </a>
                        <!-- Return Reason Button -->
                        <div v-if="item.return_reason" class="tw-relative">
                            <button 
                                @click="handleReturnReasonClick"
                                :class="[!item.return_reason_read_at ? 'tw-border-red-300 tw-bg-red-100 tw-animate-pulse' : 'tw-bg-gray-100']"
                                class="tw-flex tw-items-center tw-justify-center tw-w-8 tw-h-8 tw-rounded-full tw-bg-gray-100 hover:tw-bg-gray-200 tw-duration-200 tw-border tw-border-solid tw-border-gray-200 hover:tw-border-gray-400"
                            >
                                <icon icon="duo-icons:alert-triangle" class="tw-text-base tw-text-gray-600" :class="[!item.return_reason_read_at ? 'tw-text-red-700' : 'tw-text-gray-600']" />
</button>
                            <div 
                                v-if="showReturnReasonTooltip"
                                ref="tooltipRef"
                                class="tw-absolute tw-z-50 tw-bottom-full tw-left-1/2 tw-transform tw--translate-x-1/2 tw-mb-2 tw-px-3 tw-py-2 tw-bg-white tw-rounded-md tw-shadow-lg tw-border tw-border-solid tw-border-gray-200 tw-text-xs tw-whitespace-nowrap"
                            >
                                {{ item.return_reason }}
                            </div>
                        </div>
                    </template>
                    <template v-else>
                        <p class="tw-truncate tw-max-w-[100px] tw-text-xs tw-rounded tw-w-fit tw-px-4 tw-py-1 tw-mx-auto">
                            {{ $t('common.without') }}
                        </p>
                    </template>
                </div>
            </td>
            
            <!-- Nawris code column -->
            <td v-else-if="colName === 'external_tracking'" class="tw-px-5 tw-py-3 tw-text-sm">
                <p class="tw-truncate tw-max-w-[100px] tw-font-medium">{{ item.external_tracking || '-' }}</p>
            </td>
            
            <!-- Agent notes column -->
            <td v-else-if="colName === 'agent_notes'" class="tw-px-5 tw-py-3 tw-text-sm">
                <p class="tw-truncate tw-max-w-[150px]">{{ item.agent_notes || '-' }}</p>
            </td>
            
            <!-- Delivery status column -->
            <td v-else-if="colName === 'delivery_status'" class="tw-px-5 tw-py-3 tw-text-sm">
                <p class="tw-truncate tw-max-w-[100px] tw-text-xs tw-rounded tw-w-fit tw-px-4 tw-py-1 tw-mx-auto" :class="[delivery_status.color]" v-if="delivery_status?.name">
                    {{ $t(delivery_status.name) }}
                </p>
            </td>
            
            <!-- Delivery time column -->
            <td v-else-if="colName === 'delivery_time'" class="tw-px-5 tw-py-3 tw-text-sm">
                <div class="tw-flex tw-justify-center tw-gap-1">
                    <p class="tw-truncate tw-max-w-[70px] tw-text-xs" v-if="!item.delivery_started_at && !item.delivery_ended_at">-</p>
                    <p class="tw-truncate tw-max-w-[70px] tw-text-xs tw-text-amber-500" v-else-if="item.delivery_started_at && !item.delivery_ended_at">{{ $t('common.ongoing') }}</p>
                    <p class="tw-truncate tw-max-w-[70px] tw-text-xs tw-text-emerald-500 tw-font-semibold" v-else-if="item.delivery_started_at && item.delivery_ended_at">{{ moment.duration(moment(item.delivery_started_at).diff(moment(item.delivery_ended_at))).locale($i18n.locale).humanize() }}</p>
                </div>
            </td>
            
            <!-- Created at column -->
            <td v-else-if="colName === 'created_at'" class="tw-px-5 tw-py-3 tw-text-sm">
                <p class="tw-truncate tw-max-w-[100px] tw-text-xs">{{ formatDate(item.created_at) }}</p>
            </td>
            
            <!-- Updated at column -->
            <td v-else-if="colName === 'updated_at'" class="tw-px-5 tw-py-3 tw-text-sm">
                <p class="tw-truncate tw-max-w-[100px] tw-text-xs">{{ formatDate(item.updated_at) }}</p>
            </td>
            
            <!-- Reconfirmed at column -->
            <td v-else-if="colName === 'reconfirmed_at'" class="tw-px-5 tw-py-3 tw-text-sm">
                <p class="tw-truncate tw-max-w-[100px] tw-text-xs">{{ item.reconfirmed_at ? formatDate(item.reconfirmed_at) : '-' }}</p>
            </td>
            
            <!-- Actions column -->
            <td v-else-if="colName === 'actions'" class="tw-px-5">
                <OrderTableActions :item="item" />
            </td>
        </template>
    </tr>
</template>

<script setup>
import { defineProps, toRef, inject, computed, ref} from "vue";
import OrderTableActions from "./OrderTableActions";
import { getConfirmation, getDelivery, getReconfirmation } from "@/utils/status";
import { useStateStore } from "@/stores/app/state";
import { getOrderLink } from '@/utils/nawris'
import { CURRENCY } from "@/config/app";
import moment from "moment";
import { returnReasonRead } from "@/api/orders";

const props = defineProps({
    item: {
        required: true
    },
    visibleColumns: {
        type: Object,
        default: () => ({})
    },
    columnOrder: {
        type: Array,
        default: () => []
    }
});

const item = toRef(props, "item");
const stateStore = useStateStore();
const active = inject('active');

const confirmation = getConfirmation(item.value.agent_status);
const reconfirmation = getReconfirmation(item.value.followup_status);
const delivery_status = getDelivery(item.value.delivery_status);

const delivery = stateStore.alldeliveries.find(d => d.id == item.value.delivery_id);

const showReturnReasonTooltip = ref(false);
const tooltipRef = ref(null);

const handleReturnReasonClick = async (event) => {
    event.stopPropagation();
    showReturnReasonTooltip.value = !showReturnReasonTooltip.value;

    if(item.value.return_reason == 'dont read x') {
        const response = await returnReasonRead(item.value.id);
        if (response.data.code === 'SUCCESS') {
            item.value.return_reason_read_at = response.data.order.return_reason_read_at;
        }
    }
};


// Get the order of visible columns
const orderedVisibleColumns = computed(() => {
    // Extract the visible column names (those that are not false)
    const visibleColNames = Object.keys(props.visibleColumns)
        .filter(colName => props.visibleColumns[colName] !== false);
    
    // If we have a column order from the parent, use it to sort the visible columns
    if (props.columnOrder && props.columnOrder.length > 0) {
        return visibleColNames.sort((a, b) => {
            const indexA = props.columnOrder.indexOf(a);
            const indexB = props.columnOrder.indexOf(b);
            
            // If both are in the order array, sort by their index
            if (indexA !== -1 && indexB !== -1) {
                return indexA - indexB;
            }
            // If only a is in the order array, it comes first
            else if (indexA !== -1) {
                return -1;
            }
            // If only b is in the order array, it comes first
            else if (indexB !== -1) {
                return 1;
            }
            // If neither is in the order array, keep original order
            return 0;
        });
    }
    
    return visibleColNames;
});

// Format date
const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = moment(dateString);
    return date.format('YYYY-MM-DD HH:mm');
};
</script>

<style></style>
